import React, { useState, useEffect } from 'react';
import PremiumLayout from '../components/layout/PremiumLayout';
import ReassignLeadModal from '../components/ReassignLeadModal';
import api from '../utils/api';
import '../styles/PremiumDashboard.css';

export default function AmbassadorsList() {
  const [ambassadors, setAmbassadors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subordinates, setSubordinates] = useState({ type: 'agenti', items: [] });
  const [selectedSubordinate, setSelectedSubordinate] = useState('all');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [ambassadorToReassign, setAmbassadorToReassign] = useState(null);

  const fetchSubordinates = async () => {
    try {
      const res = await api.get('/op/subordinates');
      if (res.data.success && res.data.items) {
        setSubordinates({ type: res.data.type || 'agenti', items: res.data.items });
      }
    } catch (err) {
      console.error('Eroare încărcare subalterni:', err);
    }
  };

  const fetchAmbassadors = async () => {
    setLoading(true);
    try {
      const params = selectedSubordinate && selectedSubordinate !== 'all' ? { subordinateId: selectedSubordinate } : {};
      const res = await api.get('/ambassadors/list', { params });
      setAmbassadors(res.data.ambassadors || []);
    } catch (err) {
      console.error('Eroare încărcare ambasadori:', err);
      setAmbassadors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubordinates();
  }, []);

  useEffect(() => {
    fetchAmbassadors();
  }, [selectedSubordinate]);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const topActions = (
    <div className="flex items-center gap-3">
      <select
        value={selectedSubordinate}
        onChange={(e) => setSelectedSubordinate(e.target.value)}
        className="agent-filter-select"
        title="Filtrează pe subaltern"
      >
        <option value="all">Toată echipa</option>
        {(subordinates.items || []).map((item) => (
          <option key={item.id} value={item.id}>
            {item.numeComplet || item.email || `#${item.id}`}
          </option>
        ))}
      </select>
    </div>
  );

  if (loading && ambassadors.length === 0) {
    return (
      <PremiumLayout title="Ambasadori" subtitle="Clienți care recomandă" variant="op" topActions={null}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title="Ambasadori" subtitle="Clienți care recomandă serviciile și link-ul lor unic" variant="op" topActions={topActions}>
      <div className="space-y-6">
        <div className="section-header" style={{ marginBottom: 0 }}>
          <span className="section-title">Lista Ambasadori</span>
          <span className="section-line" />
        </div>

        {ambassadors.length === 0 ? (
          <div className="agent-leads-table-wrap p-12 text-center">
            <div className="text-6xl mb-4 opacity-50">🌟</div>
            <p className="text-[var(--text)] font-medium">Nu există ambasadori pentru filtrul selectat.</p>
            <button
              type="button"
              onClick={() => setSelectedSubordinate('all')}
              className="mt-4 px-4 py-2 rounded-lg font-medium bg-[var(--accent-glow)] text-[#8ba4fc] border border-[rgba(79,110,247,0.2)] hover:opacity-90"
            >
              Arată toată echipa
            </button>
          </div>
        ) : (
          <div className="agent-leads-table-wrap">
            <div className="overflow-x-auto">
              <table className="agent-leads-table">
                <thead>
                  <tr>
                    <th>Nume Ambasador</th>
                    <th>Email</th>
                    <th>Telefon</th>
                    <th>Cod Unic</th>
                    <th>Recomandări Aduse</th>
                    <th>Dată Înscriere</th>
                    <th>Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {ambassadors.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div className="font-medium text-[var(--text)]">{a.numeComplet}</div>
                      </td>
                      <td className="text-[var(--text-muted)]">{a.email}</td>
                      <td className="text-[var(--text-soft)] font-mono text-sm">{a.telefon}</td>
                      <td>
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/40">
                          {a.ambassadorCode}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-3 py-1 text-xs font-bold rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            {a.referralCount} / {a.ambassadorTarget ?? 3}
                          </span>
                          {a.referralCount >= (a.ambassadorTarget ?? 3) && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/40" title="Target atins">
                              🏆 Target Atins!
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-[var(--text-muted)] text-sm">{formatDate(a.dataInscriere)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => { setAmbassadorToReassign(a); setShowReassignModal(true); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30"
                          title="Transferă la alt agent"
                        >
                          ⇄ Transfer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showReassignModal && ambassadorToReassign && (
          <ReassignLeadModal
            lead={{
              id: ambassadorToReassign.id,
              numeComplet: ambassadorToReassign.numeComplet,
              isAmbassador: true,
              ambassadorCode: ambassadorToReassign.ambassadorCode,
            }}
            onClose={() => { setShowReassignModal(false); setAmbassadorToReassign(null); }}
            onSuccess={() => { fetchAmbassadors(); setShowReassignModal(false); setAmbassadorToReassign(null); }}
          />
        )}
      </div>
    </PremiumLayout>
  );
}
